import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

export type PersistedScrollState = {
    scrollTop: number;
    itemCount: number;
};

function buildStorageKey(containerId: string, locationKey: string): string {
    return `scroll-restoration:${containerId}:${locationKey}`;
}

/**
 * Reads a previously persisted scroll position/item-count for a given container and
 * history-entry key. Safe to call synchronously during render (e.g. a useState
 * initializer) since it only touches sessionStorage.
 */
export function getPersistedScrollState(containerId: string, locationKey: string): PersistedScrollState | undefined {
    if (typeof window === 'undefined') return undefined;
    try {
        const raw = window.sessionStorage.getItem(buildStorageKey(containerId, locationKey));
        if (!raw) return undefined;
        const parsed = JSON.parse(raw) as Partial<PersistedScrollState>;
        if (typeof parsed.scrollTop !== 'number' || typeof parsed.itemCount !== 'number') return undefined;
        return { scrollTop: parsed.scrollTop, itemCount: parsed.itemCount };
    } catch {
        return undefined;
    }
}

export type UseScrollRestorationOptions = {
    /** DOM id of the scrollable container to persist/restore scrollTop on. */
    containerId: string;
    /** True once the container's content has loaded enough to make restoring scrollTop meaningful. */
    ready: boolean;
    /** Current number of loaded items, persisted alongside scrollTop for paginated lists. */
    itemCount?: number;
};

const RESTORE_ATTEMPT_FRAMES = 3;

/**
 * Persists the scrollTop (and item count, for paginated lists) of a container keyed by
 * the current history entry, and restores it once the caller reports the content is
 * `ready`. Pair with `getPersistedScrollState` to read the saved item count up front so
 * a paginated list can request enough items in its initial fetch to make the restored
 * scroll position land somewhere real.
 */
export function useScrollRestoration({ containerId, ready, itemCount = 0 }: UseScrollRestorationOptions): void {
    const location = useLocation();
    const locationKey = location.key;
    const itemCountRef = useRef(itemCount);
    itemCountRef.current = itemCount;
    const hasRestoredRef = useRef(false);

    useEffect(() => {
        hasRestoredRef.current = false;
    }, [containerId, locationKey]);

    useEffect(() => {
        const container = document.getElementById(containerId);
        if (!container) return;

        let scheduled = false;
        const persist = () => {
            scheduled = false;
            const state: PersistedScrollState = { scrollTop: container.scrollTop, itemCount: itemCountRef.current };
            try {
                window.sessionStorage.setItem(buildStorageKey(containerId, locationKey), JSON.stringify(state));
            } catch {
                // sessionStorage can throw in private-browsing/quota-exceeded situations; scroll restore is best-effort.
            }
        };
        const onScroll = () => {
            if (scheduled) return;
            scheduled = true;
            requestAnimationFrame(persist);
        };

        container.addEventListener('scroll', onScroll, { passive: true });
        return () => container.removeEventListener('scroll', onScroll);
    }, [containerId, locationKey]);

    useEffect(() => {
        if (!ready || hasRestoredRef.current) return;
        const saved = getPersistedScrollState(containerId, locationKey);
        hasRestoredRef.current = true;
        if (!saved) return;

        const container = document.getElementById(containerId);
        if (!container) return;

        let frame = 0;
        const applyScroll = () => {
            container.scrollTop = saved.scrollTop;
            frame += 1;
            if (frame < RESTORE_ATTEMPT_FRAMES) {
                requestAnimationFrame(applyScroll);
            }
        };
        requestAnimationFrame(applyScroll);
    }, [containerId, locationKey, ready]);
}
